import { Box, styled } from '@mui/material';
import { IconAdd } from 'assets/img';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, LwButton, Page, SearchBar, Toolbox, useOnScrollDown } from 'redesign';
import { RenderUnsettledUI } from 'shared/components';
import { MonthDateControl } from 'shared/components/date-control-toolbox/month-date-control/month-date-control';
import { useDebounce } from 'shared/hooks/use-debounce';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';
import { AvailabilityInfoButton } from './availability-info-button';
import { AvailabilityCalendarGrid } from '../availability-calendar-grid';
import { useAvailabilityActionHandler } from '../hooks/use-availability-action-handler';
import { useGetAvailableWorkers } from '../hooks/use-get-workers-availability';
import {
  AvailabilityPageParamsProvider,
  useAvailabilityPageParamsContext,
} from '../providers/availability-page-params-provider';
import { AvailabilitySidebarFormProvider } from '../wizard/availability-sidebar';

export const AvailabilityPageDataProvider = () => (
  <AvailabilityPageParamsProvider>
    <AvailabilityPage />
  </AvailabilityPageParamsProvider>
);

export const AvailabilityPage = () => {
  const { t } = useTranslation('all-platforms-common');
  const { filters, setFilters } = useAvailabilityPageParamsContext();
  const {
    isSideBarOpen,
    availabilityForEdit,
    handleEditAvailability,
    handleCreateAvailability,
    setIsSideBarOpen,
    mode,
  } = useAvailabilityActionHandler();

  const debouncedWorkerSearchValue = useDebounce(filters.search, 300);

  //May later support different modes, for now we hardcode to month
  const calendarMode = 'month';

  const query = useGetAvailableWorkers({
    search: debouncedWorkerSearchValue,
    fromDate: filters.date ?? DateTime.now(),
    pageSizeParam: DEFAULT_PAGE_SIZE,
  });

  const flatData = useMemo(() => {
    const data = query.data?.pages.flatMap((data) => data.items) ?? [];
    return data;
  }, [query.data]);

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: query,
    flatData,
  });

  const selectedDate = filters.date ?? DateTime.now();

  const handleChangeDate = (newDate: DateTime) => {
    setFilters({ date: newDate });
  };

  const handleChangeSearch = (search: string) => {
    setFilters({ search });
  };

  return (
    <Page
      header={
        <FlexBox>
          <Box>
            <Header titleText={t('availability_page.title')} />
          </Box>
          <CenteredBox>
            <MonthDateControl
              date={selectedDate}
              onChange={handleChangeDate}
              onToday={() => handleChangeDate(DateTime.now())}
              todayPosition="right"
            />
          </CenteredBox>
        </FlexBox>
      }
      toolbox={
        <Toolbox alignCenter>
          <LwButton
            onClick={handleCreateAvailability}
            startIcon={<IconAdd />}
            color="primary"
            iconColorMode="fill"
          >
            {t('availability_page.create_availability') || 'Create Availability'}
          </LwButton>
          <SearchBar
            onChange={handleChangeSearch}
            initialValue={filters.search}
            placeholder="Filter workers"
          />
          <AvailabilityInfoButton />
        </Toolbox>
      }
    >
      <StyledCalendarWrapper>
        {query.status === 'success' ? (
          <AvailabilityCalendarGrid
            flatData={flatData}
            selectedDate={selectedDate}
            calendarMode={calendarMode}
            onScroll={(e) => {
              if (e.target instanceof HTMLDivElement) {
                fetchIfBottomScrolled(e.target);
              }
            }}
            handleAvailabilityEditClick={handleEditAvailability}
          />
        ) : (
          RenderUnsettledUI({ data: query })
        )}
      </StyledCalendarWrapper>
      {isSideBarOpen && (
        <AvailabilitySidebarFormProvider
          isOpen={isSideBarOpen}
          onClose={() => setIsSideBarOpen(false)}
          availabilityForEdit={availabilityForEdit}
          fromDate={selectedDate}
          searchTerm={debouncedWorkerSearchValue}
          mode={mode}
        />
      )}
    </Page>
  );
};

const FlexBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const CenteredBox = styled(Box)(() => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
}));

const StyledCalendarWrapper = styled(Box)({
  height: '100%',
  width: '100%',
});
